import type { InputHTMLAttributes } from "react";
import type { FieldErrors, RegisterOptions } from "react-hook-form";
import { useFormContext } from "react-hook-form";

import getRequiredMessage from "~/util/getFormItemRequiredMessage";
import repeaterErrorHandler from "~/util/repeaterErrorHandler";
import { sanitizeFormLabel } from "~/util/sanitizeHtml";

import ErrorMessage from "../ErrorMessage";

interface CheckboxProps {
  disabled?: boolean;
  description?: string | null | undefined;
  id: string;
  inGroup?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label: string;
  large?: boolean;
  small?: boolean;
  name: string;
  required?: boolean | undefined | null | string;
  rules?: RegisterOptions;
  value?: string;
}

/**
 * This component is a React Hook Form wrapper for the checkbox element.
 *
 * Note: If links are needed in the label, they must be passed in as HTML using anchor tags.
 * e.g. label="I agree to the <a href='https://www.google.com'>terms and conditions</a>."
 */
const Checkbox = ({
  disabled = false,
  description = null,
  id,
  inGroup = false,
  inputProps = {},
  label,
  large = false,
  small = false,
  name,
  required = false,
  rules = {},
  value,
}: CheckboxProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const errorMessage = repeaterErrorHandler(name, errors as FieldErrors);

  let baseClasses = "sm:col-span-4";
  if (disabled && !inGroup) baseClasses += " opacity-50";

  let inputClasses = "h-4 w-4 rounded-sm text-blue-600 focus:ring-blue-600";
  let labelClasses = "block text-sm leading-6 text-gray-900";

  if (small) {
    labelClasses += " ml-2 text-xs";
  } else {
    labelClasses += " ml-3 text-sm";
  }

  if (errorMessage) {
    inputClasses += " border-red-300 ";
    labelClasses += "";
  } else {
    inputClasses += " border-gray-300";
    labelClasses += " text-gray-900";
  }

  if (large) {
    baseClasses = baseClasses += " mb-2";
  }

  const requiredMessage = getRequiredMessage(required);

  return (
    <div className={baseClasses}>
      <div className="relative flex items-start">
        <div className="flex h-6 items-center">
          <input
            {...(description && {
              "aria-describedby": `${id}-description`,
            })}
            aria-invalid={Boolean(errorMessage)}
            aria-required={Boolean(required)}
            className={inputClasses}
            disabled={disabled}
            id={id}
            type="checkbox"
            value={value}
            {...inputProps}
            {...register(name, {
              required: requiredMessage,
              ...rules,
            })}
          />
        </div>
        <div className={labelClasses}>
          <label
            htmlFor={id}
            dangerouslySetInnerHTML={{ __html: sanitizeFormLabel(label) }}
            className="leading-6 font-medium text-gray-900"
          />
          {description && (
            <p id={`${id}-description`} className="text-gray-500">
              {description}
            </p>
          )}
        </div>
      </div>
      {errorMessage && !inGroup && (
        <ErrorMessage id={`${id}-error`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

export default Checkbox;
